
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface VideoResponse {
  id: string;
  user_id: string;
  original_post_id: string | null;
  video_url: string;
  response_text: string | null;
  likes_count: number;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string | null;
    farm_name: string | null;
    avatar_url: string | null;
  } | null;
  is_liked?: boolean;
}

export const useVideoResponses = (postId?: string) => {
  const [videoResponses, setVideoResponses] = useState<VideoResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchVideoResponses = async () => {
    try {
      let query = supabase
        .from('video_responses')
        .select(`
          *
        `)
        .order('created_at', { ascending: false });

      if (postId) {
        query = query.eq('original_post_id', postId);
      }

      const { data: videoResponsesData, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!videoResponsesData) {
        setVideoResponses([]);
        return;
      }

      // Fetch profiles separately for each video response
      const userIds = videoResponsesData.map(response => response.user_id);
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, username, farm_name, avatar_url')
        .in('user_id', userIds);

      // Create a map of user_id to profile
      const profilesMap = new Map();
      profilesData?.forEach(profile => {
        profilesMap.set(profile.user_id, profile);
      });

      // Check which responses are liked by current user
      const { data: { user } } = await supabase.auth.getUser();
      let likedIds = new Set();
      
      if (user && videoResponsesData.length > 0) {
        const { data: likes } = await supabase
          .from('video_response_likes')
          .select('video_response_id')
          .eq('user_id', user.id)
          .in('video_response_id', videoResponsesData.map(r => r.id));

        likedIds = new Set(likes?.map(l => l.video_response_id));
      }
      
      const responsesWithProfiles = videoResponsesData.map(response => ({
        ...response,
        profiles: profilesMap.get(response.user_id) || null,
        is_liked: likedIds.has(response.id)
      }));

      setVideoResponses(responsesWithProfiles);
    } catch (error) {
      console.error('Error fetching video responses:', error);
      toast({
        title: "Error",
        description: "Failed to load video responses",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createVideoResponse = async (
    originalPostId: string | null,
    videoUrl: string,
    responseText: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('video_responses')
        .insert({
          user_id: user.id,
          original_post_id: originalPostId,
          video_url: videoUrl,
          response_text: responseText
        })
        .select()
        .single();

      if (error) throw error;

      // Fetch the profile for this user
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_id, username, farm_name, avatar_url')
        .eq('user_id', user.id)
        .single();

      const responseWithProfile = {
        ...data,
        profiles: profileData,
        is_liked: false
      };

      setVideoResponses(prev => [responseWithProfile, ...prev]);
      
      toast({
        title: "Success",
        description: "Video response created successfully!",
      });

      return data;
    } catch (error) {
      console.error('Error creating video response:', error);
      toast({
        title: "Error",
        description: "Failed to create video response",
        variant: "destructive",
      });
      throw error;
    }
  };

  const toggleLike = async (responseId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const response = videoResponses.find(r => r.id === responseId);
      if (!response) return;

      if (response.is_liked) {
        // Unlike
        const { error } = await supabase
          .from('video_response_likes')
          .delete()
          .eq('user_id', user.id)
          .eq('video_response_id', responseId);

        if (error) throw error;
      } else {
        // Like
        const { error } = await supabase
          .from('video_response_likes')
          .insert({
            user_id: user.id,
            video_response_id: responseId
          });

        if (error) throw error;
      }

      // Update local state
      setVideoResponses(prev => prev.map(response => 
        response.id === responseId 
          ? {
              ...response,
              is_liked: !response.is_liked,
              likes_count: response.is_liked 
                ? response.likes_count - 1 
                : response.likes_count + 1
            }
          : response
      ));

    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        title: "Error",
        description: "Failed to update like",
        variant: "destructive",
      });
    }
  };

  const deleteVideoResponse = async (responseId: string) => {
    try {
      const { error } = await supabase
        .from('video_responses')
        .delete()
        .eq('id', responseId);

      if (error) throw error;

      setVideoResponses(prev => prev.filter(r => r.id !== responseId));
      
      toast({
        title: "Success",
        description: "Video response deleted successfully!",
      });
    } catch (error) {
      console.error('Error deleting video response:', error);
      toast({
        title: "Error",
        description: "Failed to delete video response",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchVideoResponses();
  }, [postId]);

  return {
    videoResponses,
    loading,
    createVideoResponse,
    toggleLike,
    deleteVideoResponse,
    refetch: fetchVideoResponses
  };
};
